import type {SiteSettings} from '../../client';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function requiredString(value: string, message = 'This field is required'): string | null {
    return value.trim() ? null : message;
}

function requiredDate(value: string): string | null {
    if (!value.trim()) {
        return 'This field is required';
    }
    if (!DATE_PATTERN.test(value)) {
        return 'Enter a valid date (YYYY-MM-DD)';
    }
    return null;
}

function requiredEmail(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) {
        return 'This field is required';
    }
    if (!EMAIL_PATTERN.test(trimmed)) {
        return 'Enter a valid email address';
    }
    return null;
}

function requiredNumber(value: number | string, min: number): string | null {
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) {
        return 'This field is required';
    }
    if (num < min) {
        return min === 0 ? 'Must be 0 or greater' : `Must be at least ${min}`;
    }
    return null;
}

const IBAN_PATTERN = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;
const ACCOUNT_HOLDER_PATTERN = /^[A-Za-z0-9 .,'-]+$/;
const ACCOUNT_HOLDER_ERROR =
    "Account holder may only contain ASCII letters, numbers, spaces, and . , ' -";

function optionalIban(value: string): string | null {
    const normalized = value.replace(/\s/g, '').toUpperCase();
    if (!normalized) {
        return null;
    }
    if (!IBAN_PATTERN.test(normalized)) {
        return 'Enter a valid IBAN';
    }
    return null;
}

function optionalAccountHolder(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }
    if (!ACCOUNT_HOLDER_PATTERN.test(trimmed)) {
        return ACCOUNT_HOLDER_ERROR;
    }
    return null;
}

function optionalRemittance(value: string): string | null {
    if (value.length > 140) {
        return 'Remittance must be at most 140 characters';
    }
    return null;
}

const TSHIRT_SIZES_REQUIRED_MESSAGE =
    'At least one size list (women or men) is required';

function hasTshirtSizes(value: string[]): boolean {
    return value.some((entry) => entry.trim());
}

function tshirtSizeListErrors(
    woman: string[],
    men: string[],
): {tshirts_woman?: string; tshirts_men?: string} {
    if (hasTshirtSizes(woman) || hasTshirtSizes(men)) {
        return {};
    }
    return {
        tshirts_woman: TSHIRT_SIZES_REQUIRED_MESSAGE,
        tshirts_men: TSHIRT_SIZES_REQUIRED_MESSAGE,
    };
}

export function getSiteSettingsFormErrors(values: SiteSettings): Partial<Record<keyof SiteSettings, string>> {
    const errors: Partial<Record<keyof SiteSettings, string>> = {};

    const setError = (field: keyof SiteSettings, message: string | null) => {
        if (message) {
            errors[field] = message;
        }
    };

    setError('eventcount', requiredNumber(values.eventcount, 1));
    setError('registration_max', requiredNumber(values.registration_max, 1));
    setError('registration_fee', requiredNumber(values.registration_fee, 0));
    setError('payment_reminder_days', requiredNumber(values.payment_reminder_days, 1));
    setError('video_url', requiredString(values.video_url));
    setError('color', requiredString(values.color));
    setError('domain', requiredString(values.domain));
    setError('mail', requiredEmail(values.mail));
    setError('saniphone', requiredString(values.saniphone));
    setError('address_name', requiredString(values.address_name));
    setError('address_street', requiredString(values.address_street));
    setError('address_city', requiredString(values.address_city));
    setError('address_zipcode', requiredString(values.address_zipcode));
    setError('address_maps_link', requiredString(values.address_maps_link));
    setError('parking_label', requiredString(values.parking_label));
    setError('parking_url', requiredString(values.parking_url));
    setError('date_start', requiredDate(values.date_start));
    setError('date_end', requiredDate(values.date_end));
    setError('registration_last_register', requiredDate(values.registration_last_register));
    setError('registration_last_cancel', requiredDate(values.registration_last_cancel));
    const tshirtErrors = tshirtSizeListErrors(values.tshirts_woman, values.tshirts_men);
    setError('tshirts_woman', tshirtErrors.tshirts_woman ?? null);
    setError('tshirts_men', tshirtErrors.tshirts_men ?? null);

    if (values.maintenance_active) {
        setError(
            'maintenance_text',
            requiredString(
                values.maintenance_text,
                'Maintenance text is required when maintenance mode is active',
            ),
        );
    }

    setError('bank_account_holder', optionalAccountHolder(values.bank_account_holder ?? ''));
    setError('bank_iban', optionalIban(values.bank_iban ?? ''));
    setError('bank_remittance', optionalRemittance(values.bank_remittance ?? ''));

    return errors;
}

export function isSiteSettingsFormValid(values: SiteSettings): boolean {
    return Object.keys(getSiteSettingsFormErrors(values)).length === 0;
}
